import type { ApiErrorResponse, ApiError } from '@personnel/application/errors/apiErrorTypes';
import type { ErrorCode } from '@personnel/domain/errors/errorCodes';
import { ERROR_CODES, ERROR_INTERPOLATION_PATTERNS, getInterpolationField } from '@personnel/domain/errors/errorCodes';

/**
 * Extracted error information for display
 */
export interface ExtractedError {
  code: string;
  message: string;
  interpolationData?: Record<string, string>;
}

/**
 * Checks if status code is a server error (5xx)
 *
 * @param statusCode - HTTP status code
 * @returns True if status is 5xx
 */
const isServerError = (statusCode: number): boolean => {
  return statusCode >= 500 && statusCode < 600;
};

/**
 * Extracts error code and message from API error response
 *
 * Strategy:
 * - 4xx errors: Use error.code from backend (always provided)
 * - 5xx errors: Use ERROR_CODES.COMMON.SERVER_ERROR
 *
 * @param error - API error response
 * @param statusCode - HTTP status code
 * @returns Extracted error information for toast display
 *
 * @example
 * // 4xx - Backend provides code
 * extractErrorInfo({ errors: [{ code: "roles.PermissionsNotFound", ... }] }, 400)
 * // Returns: { code: "roles.PermissionsNotFound", message: "...", interpolationData: {...} }
 *
 * // 5xx - Generic server error
 * extractErrorInfo({ errors: [...] }, 500)
 * // Returns: { code: "common.ServerError", message: "..." }
 */
export const extractErrorInfo = (error: ApiErrorResponse, statusCode: number): ExtractedError => {
  // Get first error
  const firstError: ApiError | undefined = error.errors[0];

  if (!firstError) {
    return {
      code: ERROR_CODES.COMMON.UNEXPECTED_ERROR,
      message: 'An unexpected error occurred',
    };
  }

  // Strategy: 5xx errors are generic, 4xx errors have specific codes
  let errorCode: string;

  if (isServerError(statusCode)) {
    // 5xx: Always use generic ServerError
    errorCode = ERROR_CODES.COMMON.SERVER_ERROR;
  } else {
    // 4xx: Use backend error code (always provided for client errors)
    errorCode = firstError.code || ERROR_CODES.COMMON.UNEXPECTED_ERROR;
  }

  // Extract interpolation data for dynamic messages (only for 4xx with codes)
  const interpolationData = !isServerError(statusCode) ? extractInterpolationData(firstError) : undefined;

  return {
    code: errorCode,
    message: firstError.message || 'An unexpected error occurred',
    interpolationData,
  };
};

/**
 * Extracts dynamic data from error message for i18n interpolation
 *
 * Only used for 4xx errors with specific codes.
 * Uses ERROR_INTERPOLATION_PATTERNS dictionary to match known patterns.
 *
 * @param error - Single API error
 * @returns Object with interpolation data
 *
 * @example
 * // Message: "The following permissions do not exist: store-admin, backoffice.module"
 * extractInterpolationData({ code: "roles.PermissionsNotFound", message: "..." })
 * // Returns: { permissions: 'store-admin, backoffice.module' }
 */
const extractInterpolationData = (error: ApiError): Record<string, string> | undefined => {
  const errorCode = error.code as ErrorCode;

  // Check if this error code has an interpolation pattern
  const pattern = ERROR_INTERPOLATION_PATTERNS[errorCode];
  if (!pattern) {
    return undefined;
  }

  // Try to extract data using the pattern
  const match = error.message.match(pattern);
  if (!match || !match[1]) {
    return undefined;
  }

  // Get the field name for this error code
  const fieldName = getInterpolationField(errorCode);
  if (!fieldName) {
    return undefined;
  }

  return {
    [fieldName]: match[1].trim(),
  };
};

/**
 * Formats multiple errors into a single message
 *
 * @param errors - Array of API errors
 * @returns Formatted error message
 *
 * @example
 * formatMultipleErrors([{ message: 'Error 1' }, { message: 'Error 2' }])
 * // Returns: "• Error 1\n• Error 2"
 */
export const formatMultipleErrors = (errors: ApiError[]): string => {
  if (errors.length === 1) {
    return errors[0].message;
  }

  return errors.map((err) => `• ${err.message}`).join('\n');
};

/**
 * Gets the i18n translation key from error code
 *
 * Mapping logic:
 * - "roles.PermissionsNotFound" → { namespace: 'roles', key: 'errors.PermissionsNotFound' }
 * - "common.ServerError" → { namespace: 'personnel', key: 'errors.common.ServerError' }
 * - "employees.EmailExists" → { namespace: 'employees', key: 'errors.EmailExists' }
 *
 * @param errorCode - Error code from backend (4xx) or mapped (5xx)
 * @returns Object with namespace and key for translation
 *
 * @example
 * getTranslationKey('roles.PermissionsNotFound')
 * // Returns: { namespace: 'roles', key: 'errors.PermissionsNotFound' }
 *
 * getTranslationKey('common.ServerError')
 * // Returns: { namespace: 'personnel', key: 'errors.common.ServerError' }
 */
export const getTranslationKey = (errorCode: string): { namespace: string; key: string } => {
  const [domain, ...rest] = errorCode.split('.');

  if (rest.length > 0) {
    const errorName = rest.join('.');

    // Map domain to correct namespace
    switch (domain) {
      case 'roles':
        return { namespace: 'roles', key: `errors.${errorName}` };
      case 'employees':
        return { namespace: 'personnel-employees', key: `errors.${errorName}` };
      case 'users':
        return { namespace: 'personnel-employees', key: `errors.${errorName}` };
      case 'common':
        // Common errors live in personnel namespace
        return { namespace: 'personnel', key: `errors.common.${errorName}` };
      default:
        // Unknown domain - fallback to personnel common errors
        return { namespace: 'personnel', key: 'errors.common.UnexpectedError' };
    }
  }

  // No domain specified - use personnel common errors
  return { namespace: 'personnel', key: `errors.common.${errorCode}` };
};
